import type { HexMapState } from '../../../engine/render/renderer';
import type { ContentBuilder } from '../../ui/ContentBuilder';
import { BUILDING_DEFS } from '../../../engine/economy/buildingDefs';
import {
    getBuildingSlotsLimit,
    getBuildingSlotsUsed,
} from '../../../engine/economy/settlementLimits';
import type { SettlementView } from '../utils';
import { getBuildActionStatus } from '../../../engine/action/buildActionMeta';
import {
    ACTION__MODAL__TAB__BUILDINGS__CORE,
    ACTION__MODAL__TAB__BUILDINGS__FOOD,
    ACTION__MODAL__TAB__BUILDINGS__INDUSTRY,
    ACTION__MODAL__TAB__BUILDINGS__MINING,
    ACTION__MODAL__TAB__BUILDINGS__CRAFT,
    ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
    ACTION__SETTLEMENT__BUILD_FARM,
    ACTION__SETTLEMENT__BUILD_HUNTING_HUT,
    ACTION__SETTLEMENT__BUILD_FISHING_HUT,
    ACTION__SETTLEMENT__BUILD_SMOKEHOUSE,
    ACTION__SETTLEMENT__BUILD_WEAVER,
    ACTION__SETTLEMENT__BUILD_BLACKSMITH,
    ACTION__SETTLEMENT__BUILD_GOLD_MINE,
    ACTION__SETTLEMENT__BUILD_MINT,
    ACTION__SETTLEMENT__BUILD_CARAVAN_POST,
    ACTION__SETTLEMENT__BUILD_STABLE,
    ACTION__SETTLEMENT__BUILD_HOUSE,
    ACTION__SETTLEMENT__BUILD_STONE_HOUSE,
    ACTION__SETTLEMENT__BUILD_LUMBERJACK,
    ACTION__SETTLEMENT__BUILD_SAWMILL,
    ACTION__SETTLEMENT__BUILD_QUARRY,
    ACTION__SETTLEMENT__BUILD_MASONRY,
    ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT,
    ACTION__SETTLEMENT__BUILD_IRON_MINE,
    ACTION__SETTLEMENT__BUILD_SMELTER,
    ACTION__SETTLEMENT__BUILD_BARRACKS,
    ACTION__SETTLEMENT__CANCEL_CONSTRUCTION_PREFIX,
} from '../../../engine/input/actionIds';
import { t } from '../../../i18n/translations';
import {
    BoxItem,
    ButtonItem,
    HoverGridItem,
    LinearLayoutItem,
    SpacerItem,
    TextItem,
    type UiItem,
} from '../../ui/items';

type BuildingEntry = {
    id: string;
    labelKey: string;
    count: number;
    cost: Record<string, number>;
    enabled: boolean;
};

type BuildingGridTile = {
    id: string;
    label: string;
    count: number;
    costs: Record<string, number>;
    enabled: boolean;
    buttonLabel: string;
};

function buildBuildingCells(tiles: BuildingGridTile[]): UiItem[] {
    return tiles.map((tile) => {
        const content = new LinearLayoutItem(
            [
                new TextItem(tile.label, 'muted'),
                new TextItem(`${tile.count}`, 'body'),
                new SpacerItem(6),
                new ButtonItem(tile.id, tile.buttonLabel, tile.enabled),
            ],
            { orientation: 'vertical', gap: 0, width: 'fill' },
        );

        return new BoxItem(content, {
            padding: (t) => t.buildingGrid.padding,
            fill: (t) => t.colors.tabInactiveFill,
            stroke: (t) => t.colors.tabStroke,
            strokeWidth: 1,
            widthMode: 'fill',
        });
    });
}

export function renderBuildingsTab(
    builder: ContentBuilder,
    state: HexMapState,
    settlement: SettlementView,
    activeTabId: string | null,
): void {
    const lang = state.config.ui.language;
    builder.text(
        t(
            lang,
            'buildings.slots',
            getBuildingSlotsUsed(settlement),
            getBuildingSlotsLimit(settlement),
        ),
    );
    builder.spacer(6);

    builder.section(t(lang, 'construction.section'), (section) => {
        if (settlement.construction.queue.length === 0) {
            section.text(t(lang, 'construction.none'));
            return;
        }
        settlement.construction.queue.forEach((item, index) => {
            const def = BUILDING_DEFS[item.building];
            const label = t(lang, def?.labelKey || 'construction.section');
            const canCancel = state.config.construction.refund.allowInProgress || item.work <= 0;
            section.row({ gap: 6, weights: [3, 1], crossGravity: 'center' }, (row) => {
                row.progress(`${index + 1}. ${label}`, item.work, item.workRequired);
                row.button(
                    `${ACTION__SETTLEMENT__CANCEL_CONSTRUCTION_PREFIX}${item.id}`,
                    t(lang, 'actions.cancel'),
                    canCancel,
                );
            });
        });
    });

    const buildEntries: Record<string, BuildingEntry[]> = {
        [ACTION__MODAL__TAB__BUILDINGS__CORE]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_HOUSE,
                settlement.buildings.houses,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_STONE_HOUSE,
                settlement.buildings.stoneHouse,
            ),
        ],
        [ACTION__MODAL__TAB__BUILDINGS__FOOD]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_FARM,
                settlement.buildings.farm.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_HUNTING_HUT,
                settlement.buildings.huntingHut.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_FISHING_HUT,
                settlement.buildings.fishingHut.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_SMOKEHOUSE,
                settlement.buildings.smokehouse.qty,
            ),
        ],
        [ACTION__MODAL__TAB__BUILDINGS__INDUSTRY]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_LUMBERJACK,
                settlement.buildings.lumberjack.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_SAWMILL,
                settlement.buildings.sawmill.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_CHARCOAL_PIT,
                settlement.buildings.charcoalPit.qty,
            ),
        ],
        [ACTION__MODAL__TAB__BUILDINGS__MINING]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_QUARRY,
                settlement.buildings.quarry.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_IRON_MINE,
                settlement.buildings.ironMine.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_SMELTER,
                settlement.buildings.smelter.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_GOLD_MINE,
                settlement.buildings.goldMine.qty,
            ),
        ],
        [ACTION__MODAL__TAB__BUILDINGS__CRAFT]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_MASONRY,
                settlement.buildings.masonry.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_WEAVER,
                settlement.buildings.weaver.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_BLACKSMITH,
                settlement.buildings.blacksmith.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_MINT,
                settlement.buildings.mint.qty,
            ),
        ],
        [ACTION__MODAL__TAB__BUILDINGS__SUPPORT]: [
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_CARAVAN_POST,
                settlement.buildings.caravanPost.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_STABLE,
                settlement.buildings.stable.qty,
            ),
            buildEntry(
                state,
                settlement,
                ACTION__SETTLEMENT__BUILD_BARRACKS,
                settlement.buildings.barracks.qty,
            ),
        ],
    };

    const activeKey = activeTabId || ACTION__MODAL__TAB__BUILDINGS__CORE;
    const entries = buildEntries[activeKey] || [];

    builder.tabs([
        {
            id: ACTION__MODAL__TAB__BUILDINGS__CORE,
            label: t(lang, 'tabs.buildings.core'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__CORE,
        },
        {
            id: ACTION__MODAL__TAB__BUILDINGS__FOOD,
            label: t(lang, 'tabs.buildings.food'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__FOOD,
        },
        {
            id: ACTION__MODAL__TAB__BUILDINGS__INDUSTRY,
            label: t(lang, 'tabs.buildings.industry'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__INDUSTRY,
        },
        {
            id: ACTION__MODAL__TAB__BUILDINGS__MINING,
            label: t(lang, 'tabs.buildings.mining'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__MINING,
        },
        {
            id: ACTION__MODAL__TAB__BUILDINGS__CRAFT,
            label: t(lang, 'tabs.buildings.craft'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__CRAFT,
        },
        {
            id: ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
            label: t(lang, 'tabs.buildings.support'),
            active: activeKey === ACTION__MODAL__TAB__BUILDINGS__SUPPORT,
        },
    ]);

    builder.section(t(lang, 'buildings.section'), (section) => {
        const tiles = entries.map((entry) => ({
            id: entry.id,
            label: t(lang, entry.labelKey),
            count: entry.count,
            costs: entry.cost,
            enabled: entry.enabled,
            buttonLabel: t(lang, 'actions.build'),
        }));
        const cells = buildBuildingCells(tiles);
        section.item(
            new HoverGridItem(tiles, cells, {
                columns: (t) => t.buildingGrid.columns,
                gap: (t) => t.buildingGrid.gap,
                padding: 0,
                childWidthMode: 'fill',
                alignY: 'start',
            }),
        );
    });
}

function buildEntry(
    state: HexMapState,
    settlement: SettlementView,
    actionId: string,
    count: number,
): BuildingEntry {
    const info = getBuildActionStatus(state, settlement, actionId);
    return {
        id: actionId,
        labelKey: info?.labelKey || 'buildings.section',
        count,
        cost: info?.cost || {},
        enabled: info?.enabled ?? false,
    };
}
